import classNames from 'classnames';
import { PropsWithChildren, ReactNode, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { animated, useSpring, config as springConfig } from 'react-spring';

export type CollapsibleRef = {
  toggle: (value?: boolean) => void;
  scrollToContent: () => void;
} | null;

type CollapsibleProps = PropsWithChildren<{
  trigger: (isOpen: boolean) => ReactNode;
  openAtStart?: boolean;
  showCursor?: boolean;
  isDisabled?: boolean;
  beforeHiddenContent?: ReactNode;
  afterHiddenContent?: ReactNode;
}>;

export const Collapsible = forwardRef<CollapsibleRef, CollapsibleProps>(
  (
    {
      trigger,
      openAtStart = false,
      showCursor = true,
      isDisabled = false,
      children,
      beforeHiddenContent,
      afterHiddenContent,
    }: CollapsibleProps,
    ref,
  ) => {
    // States
    const [isOpen, setIsOpen] = useState<boolean>(openAtStart);
    const [contentMaxHeight, setContentMaxHeight] = useState<number>(0);

    const contentRef = useRef<HTMLDivElement>(null);
    const animation = useSpring({
      maxHeight: isOpen ? `${contentMaxHeight}px` : '0px',
      overflow: 'hidden',
      config: springConfig.stiff,
    });

    useEffect(() => {
      if (!contentRef.current) return;

      const contentObserver = new MutationObserver(() => {
        setContentMaxHeight(contentRef.current?.scrollHeight ?? 0);
      });

      const resizeObserver = new ResizeObserver(() => {
        setContentMaxHeight(contentRef.current?.scrollHeight ?? 0);
      });

      contentObserver.observe(contentRef.current, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      resizeObserver.observe(contentRef.current);

      return () => {
        contentObserver.disconnect();
        resizeObserver.disconnect();
      }; // clean up
    }, [contentRef.current]);

    // Forward toggle function
    useImperativeHandle(
      ref,
      () => {
        return {
          toggle(value?: boolean) {
            setIsOpen(value !== undefined ? value : !isOpen);
          },
          scrollToContent() {
            if (contentRef.current) {
              contentRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          },
        };
      },
      [],
    );

    return (
      <>
        <div
          className={classNames({
            'cursor-not-allowed': isDisabled && showCursor,
            'cursor-pointer': !isDisabled && showCursor,
          })}
          onClick={() => !isDisabled && setIsOpen((prev) => !prev)}
        >
          {trigger(isOpen)}
        </div>

        {beforeHiddenContent}

        <animated.div ref={contentRef} style={animation}>
          {children}
        </animated.div>

        {afterHiddenContent}
      </>
    );
  },
);
